import axios from 'axios';
import toast from 'react-hot-toast';

// Create axios instance with default config
const api = axios.create({
  baseURL: '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const { response } = error;
    
    if (response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
      return Promise.reject(error);
    }

    // Callers that show the error inline themselves (e.g. form validation) opt out
    // of the global toast with { skipErrorToast: true } on the request config.
    if (error.config?.skipErrorToast) {
      return Promise.reject(error);
    }
    
    // One toast per distinct message — the toast id is the message, so a burst
    // of identical failures (e.g. rate-limited 429s) replaces rather than stacks.
    let message;
    // A backend-provided message is always more specific than a generic
    // per-status one (e.g. "Flight not found" vs "Resource not found.") —
    // prefer it whenever the response includes one.
    if (response?.data?.message) {
      message = response.data.message;
    } else if (response?.status === 403) {
      message = 'Access denied. You do not have permission to perform this action.';
    } else if (response?.status === 404) {
      message = 'Resource not found.';
    } else if (response?.status === 429) {
      message = 'Too many requests. Please try again later.';
    } else if (response?.status >= 500) {
      message = 'Server error. Please try again later.';
    } else if (error.code === 'ECONNABORTED') {
      message = 'Request timeout. Please check your connection.';
    } else {
      message = 'An unexpected error occurred.';
    }

    toast.error(message, { id: message });

    // Mark error as already toasted so individual catch blocks don't double-toast
    error._toasted = true;
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  getProfile: () => api.get('/auth/profile'),
  updateProfile: (userData) => api.put('/auth/profile', userData),
  changePassword: (passwordData) => api.post('/auth/change-password', passwordData),
  logout: () => api.post('/auth/logout'),
  verifyToken: () => api.get('/auth/verify'),
};

// Templates API
export const templatesAPI = {
  getAll: (params) => api.get('/templates', { params }),
  getById: (id) => api.get(`/templates/${id}`),
  getByCategory: (category) => api.get(`/templates/category/${category}`),
  getCategories: () => api.get('/templates/categories'),
  getPopular: (limit) => api.get('/templates/popular', { params: { limit } }),
  preview: (id, values, config) => api.post(`/templates/${id}/preview`, { values }, config),
  create: (templateData) => api.post('/templates', templateData),
  update: (id, templateData) => api.put(`/templates/${id}`, templateData),
  delete: (id) => api.delete(`/templates/${id}`),
  duplicate: (id, name) => api.post(`/templates/${id}/duplicate`, { name }),
};

// Submissions API
export const submissionsAPI = {
  getAll: (params) => api.get('/submissions', { params }),
  getById: (id) => api.get(`/submissions/${id}`),
  create: (submissionData) => api.post('/submissions', submissionData),
  update: (id, submissionData) => api.put(`/submissions/${id}`, submissionData),
  delete: (id) => api.delete(`/submissions/${id}`),
  search: (params) => api.get('/submissions/search', { params }),
  getStatistics: (params) => api.get('/submissions/statistics', { params }),
};

// Audio API
export const audioAPI = {
  generate: (audioData) => api.post('/audio/generate', audioData),
  generatePreview: (previewData) => api.post('/audio/preview', previewData),
  getBySubmission: (submissionId) => api.get(`/audio/submission/${submissionId}`),
  download: (id) => api.get(`/audio/download/${id}`, { responseType: 'blob' }),
  stream: (id) => api.get(`/audio/stream/${id}`, { responseType: 'blob' }),
  getVoices: (languageCode) => api.get('/audio/voices', { params: { languageCode } }),
  getPopularVoices: () => api.get('/audio/voices/popular'),
  getStatistics: (params) => api.get('/audio/statistics', { params }),
  delete: (id) => api.delete(`/audio/${id}`),
};

// Public API (no authentication required)
export const publicAPI = {
  // NEW SIMPLE QUEUE SYSTEM
  getQueueEntries: (params) => api.get('/public/queue', { params }),
  markQueueEntryCompleted: (queueId) => api.post(`/public/queue/${queueId}/completed`),
  
  // OLD METHODS (keep for compatibility)
  getAnnouncements: (params) => api.get('/public/announcements', { params }),
  getLatestAnnouncement: (params) => api.get('/public/announcements/latest', { params }),
  getStats: () => api.get('/public/stats'),
  markAsPlayed: (id) => api.post(`/public/announcements/${id}/played`),
};

// Admin API
export const adminAPI = {
  // Submissions
  getAllSubmissions: (params) => api.get('/admin/submissions', { params }),
  getPendingSubmissions: (params) => api.get('/admin/submissions/pending', { params }),
  updateSubmission: (id, data) => api.put(`/admin/submissions/${id}`, data),
  approveSubmission: (id, data) => api.post(`/admin/submissions/${id}/approve`, data),
  rejectSubmission: (id, data) => api.post(`/admin/submissions/${id}/reject`, data),
  
  // Users
  getAllUsers: (params) => api.get('/admin/users', { params }),
  createUser: (userData) => api.post('/admin/users', userData),
  updateUser: (id, userData) => api.put(`/admin/users/${id}`, userData),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),

  // Gate Assignments (user-centric)
  getUserGates: (userId) => api.get(`/admin/users/${userId}/gates`),
  setUserGates: (userId, gate_ids) => api.put(`/admin/users/${userId}/gates`, { gate_ids }),
  removeUserGate: (userId, gateId) => api.delete(`/admin/users/${userId}/gates/${gateId}`),

  // Gate Assignments (gate-centric)
  getAllGateOperators: () => api.get('/admin/gate-operators'),
  getGateOperators: (gateId) => api.get(`/admin/gates/${gateId}/operators`),
  setGateOperators: (gateId, user_ids) => api.put(`/admin/gates/${gateId}/operators`, { user_ids }),

  // Zone Assignments (user-centric)
  getUserZones: (userId) => api.get(`/admin/users/${userId}/zones`),
  setUserZones: (userId, zone_ids) => api.put(`/admin/users/${userId}/zones`, { zone_ids }),
  removeUserZone: (userId, zoneId) => api.delete(`/admin/users/${userId}/zones/${zoneId}`),

  // Zone Assignments (zone-centric)
  getAllZoneOperators: () => api.get('/admin/zone-operators'),
  getZoneOperators: (zoneId) => api.get(`/admin/zones/${zoneId}/operators`),
  setZoneOperators: (zoneId, user_ids) => api.put(`/admin/zones/${zoneId}/operators`, { user_ids }),

  // Dashboard & Reports
  getDashboardStats: (params) => api.get('/admin/dashboard', { params }),
  getReports: (params) => api.get('/admin/reports', { params }),
  
  // TTS and Audio Preview
  generatePreviewAudio: (id, settings) => api.post(`/admin/submissions/${id}/preview`, settings),
  getAvailableVoices: () => api.get('/admin/voices'),
  testTTSConnection: () => api.get('/admin/tts/test'),
  
  // Approval with settings
  approveSubmission: (id, settings) => api.post(`/admin/submissions/${id}/approve`, settings),

  // Audit log (CR 12/13)
  getAuditLog: (params) => api.get('/admin/audit-log', { params }),
};

// Users API
export const usersAPI = {
  getById: (id) => api.get(`/users/${id}`),
};

// Gates API
export const gatesAPI = {
  getAll: (config) => api.get('/gates', config),
  getById: (id) => api.get(`/gates/${id}`),
  create: (data) => api.post('/gates', data),
  update: (id, data) => api.put(`/gates/${id}`, data),
  deactivate: (id) => api.delete(`/gates/${id}`),
};

// Gate Sessions API
export const gateSessionsAPI = {
  getAll: (params) => api.get('/gate-sessions', { params }),
  getActive: (params) => api.get('/gate-sessions/active', { params }),
  getById: (id) => api.get(`/gate-sessions/${id}`),
  create: (data, config) => api.post('/gate-sessions', data, config),
  acknowledge: (id) => api.post(`/gate-sessions/${id}/acknowledge`),
  initiate: (id) => api.post(`/gate-sessions/${id}/initiate`),
  updateStatus: (id, status) => api.patch(`/gate-sessions/${id}/status`, { status }),
  skipAnnouncement: (sessionId, announcementId) =>
    api.patch(`/gate-sessions/${sessionId}/announcements/${announcementId}/skip`),
  setGateOverride: (id, gate_override) =>
    api.patch(`/gate-sessions/${id}/override`, { gate_override }),
  // Admin only — fresh pending session for a completed/cancelled flight
  reopen: (id) => api.post(`/gate-sessions/${id}/reopen`),
};

// Flights (FlightMS lookup)
export const flightsAPI = {
  lookup: (flightNumber, date, config = {}) =>
    api.get('/flights/lookup', { ...config, params: { flight_number: flightNumber, date } }),
};

// Announcement Zones API
export const announcementZonesAPI = {
  getAll: () => api.get('/announcement-zones'),
  getAllIncludingInactive: () => api.get('/announcement-zones/all'),
  create: (data) => api.post('/announcement-zones', data),
  update: (id, data) => api.put(`/announcement-zones/${id}`, data),
  deactivate: (id) => api.delete(`/announcement-zones/${id}`),
  setGates: (id, gate_ids) => api.put(`/announcement-zones/${id}/gates`, { gate_ids }),
};

// Airport Announcements API
export const airportAnnouncementsAPI = {
  submit: (data) => api.post('/airport-announcements', data),
  getAll: (params) => api.get('/airport-announcements', { params }),
  getPending: () => api.get('/airport-announcements/pending'),
  getById: (id) => api.get(`/airport-announcements/${id}`),
  preview: (id) => api.post(`/airport-announcements/${id}/preview`),
  approve: (id, data) => api.patch(`/airport-announcements/${id}/approve`, data),
  reject: (id, data) => api.patch(`/airport-announcements/${id}/reject`, data),
  edit: (id, data) => api.patch(`/airport-announcements/${id}`, data),
  broadcast: (id) => api.post(`/airport-announcements/${id}/broadcast`),
  cancel: (id) => api.delete(`/airport-announcements/${id}`),
};

// Template Requests API (CR Section 11 — Manager Template Request Workflow)
export const templateRequestsAPI = {
  submit: (data) => api.post('/template-requests', data),
  getAll: (params) => api.get('/template-requests', { params }),
  getById: (id) => api.get(`/template-requests/${id}`),
  approve: (id, data) => api.patch(`/template-requests/${id}/approve`, data),
  reject: (id, data) => api.patch(`/template-requests/${id}/reject`, data),
  requestRevision: (id, data) => api.patch(`/template-requests/${id}/request-revision`, data),
};

// Gate Queue API
export const gateQueueAPI = {
  getQueue: (gateId, sessionId, config = {}) => api.get(`/gates/${gateId}/queue`, { ...config, params: sessionId ? { session_id: sessionId } : {} }),
  reorder: (gateId, orderedIds) => api.patch(`/gates/${gateId}/queue/reorder`, { orderedIds }),
  insert: (gateId, data) => api.post(`/gates/${gateId}/queue/insert`, data),
  cancel: (gateId, annId) => api.delete(`/gates/${gateId}/queue/${annId}`),
  shift: (gateId, delta_minutes) => api.patch(`/gates/${gateId}/queue/shift`, { delta_minutes }),
  reschedule: (gateId, annId, new_time) => api.patch(`/gates/${gateId}/queue/${annId}/reschedule`, { new_time }),
};

// Destination Languages API
export const destinationLanguagesAPI = {
  lookup: (iata, country) => api.get('/destination-languages/lookup', { params: { iata, country } }),
  getSupported: () => api.get('/destination-languages/supported'),
  getOverrides: () => api.get('/destination-languages/overrides'),
  createOverride: (data) => api.post('/destination-languages/overrides', data),
  updateOverride: (id, data) => api.put(`/destination-languages/overrides/${id}`, data),
  deleteOverride: (id) => api.delete(`/destination-languages/overrides/${id}`),
};

// Flight Sync API
export const flightSyncAPI = {
  getStatus: () => api.get('/flights/sync/status'),
  runSync: () => api.post('/flights/sync'),
  getConfig: () => api.get('/flights/sync/config'),
  updateConfig: (data) => api.put('/flights/sync/config', data),
};

// Belt Assignments API (arrivals — baggage belt tracking)
export const beltAssignmentsAPI = {
  getAll: (params) => api.get('/belt-assignments', { params }),
  getActive: () => api.get('/belt-assignments/active'),
  getById: (id) => api.get(`/belt-assignments/${id}`),
  getTimeline: (id) => api.get(`/belt-assignments/${id}/timeline`),
  sendAdhoc: (id, text) => api.post(`/belt-assignments/${id}/adhoc`, { text }),
  updateScheduledTime: (id, scheduled_arrival_time) => api.patch(`/belt-assignments/${id}/scheduled-time`, { scheduled_arrival_time }),
  shiftTime: (id, delta_minutes) => api.patch(`/belt-assignments/${id}/shift`, { delta_minutes }),
  getZoneQueue: (terminal, flight) => api.get(`/belt-assignments/zones/${terminal}/queue`, { params: flight ? { flight } : {} }),
  sendZoneAdhoc: (terminal, data) => api.post(`/belt-assignments/zones/${terminal}/adhoc`, data),
  reorderZoneQueue: (terminal, orderedIds) => api.patch(`/belt-assignments/zones/${terminal}/queue/reorder`, { orderedIds }),
  shiftZoneQueue: (terminal, delta_minutes, flight_number) => api.patch(`/belt-assignments/zones/${terminal}/queue/shift`, { delta_minutes, flight_number }),
  rescheduleZoneItem: (terminal, annId, new_time) => api.patch(`/belt-assignments/zones/${terminal}/queue/${annId}/reschedule`, { new_time }),
};

// Weather Alerts API (Lightning Warning Alert workflow)
export const weatherAlertsAPI = {
  getAll: (params) => api.get('/weather-alerts', { params }),
  getActive: () => api.get('/weather-alerts/active'),
  raise: (data) => api.post('/weather-alerts', data),
  clear: (id) => api.patch(`/weather-alerts/${id}/clear`),
};

// Device API — uses device token stored in localStorage (not user JWT)
const deviceApi = (token) => ({
  headers: { Authorization: `Bearer ${token}` },
});

export const devicesAPI = {
  // Public (no token)
  activate: (device_id, pin) => api.post('/devices/activate', { device_id, pin }),

  // Device-authenticated
  getMe: (token) => api.get('/devices/me', deviceApi(token)),

  // Admin-authenticated
  getAll: () => api.get('/devices'),
  create: (data) => api.post('/devices', data),
  update: (id, data) => api.put(`/devices/${id}`, data),
  revoke: (id) => api.post(`/devices/${id}/revoke`),
  delete: (id) => api.delete(`/devices/${id}`),
};

// Utility functions
export const downloadFile = async (url, filename) => {
  try {
    const response = await api.get(url, { responseType: 'blob' });
    const blob = new Blob([response.data]);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error('Download error:', error);
    throw error;
  }
};

export const getAudioUrl = (id) => `/audio/stream/${id}`;

export default api;
